"use client";

import React from 'react';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection({ centresCount, prestatairesCount }: { centresCount: number; prestatairesCount: number }) {
  return (
    <section
      className="relative min-h-[65vh] flex items-end overflow-hidden pb-12"
      style={{ clipPath: 'ellipse(120% 98% at 50% 0%)' }}
      aria-label="Bannière principale">
      
      {/* Background image */}
      <div className="absolute inset-0 hero-bg">
        <AppImage
          src="https://img.rocket.new/generatedImages/rocket_gen_img_1a6618255-1772085553519.png"
          alt="Médecin accueillant chaleureusement un patient dans une salle de consultation lumineuse"
          fill
          priority
          className="object-cover"
          sizes="100vw" />
        
      </div>
      {/* Gradient overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(160deg, rgba(26,26,26,0.72) 0%, rgba(26,26,26,0.55) 60%, rgba(26,26,26,0.40) 100%)'
        }}
        aria-hidden="true" />
      
      {/* Decorative geometric shapes */}
      <div className="absolute top-20 right-20 w-64 h-64 rounded-full border border-white/10 opacity-30 hidden lg:block" aria-hidden="true" />
      <div className="absolute top-32 right-32 w-40 h-40 rounded-full border border-white/10 opacity-20 hidden lg:block" aria-hidden="true" />
      <div className="absolute bottom-40 left-10 w-20 h-20 rounded-full bg-white/5 hidden lg:block" aria-hidden="true" />
      {/* Content */}
      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-36 pt-32">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="hero-badge inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-4 py-2 mb-8">
            <div className="w-2 h-2 rounded-full bg-white animate-pulse" aria-hidden="true" />
            <span className="text-white text-xs font-medium tracking-wider uppercase" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Réseau de soins au tarif conventionné
            </span>
          </div>

          {/* H1 */}
          <h1
            className="hero-title hero-title-text text-white leading-tight mb-6"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.02em'
            }}>
            
            Vos soins,<br />
            <span className="text-white/90">notre priorité.</span>
          </h1>

          {/* Subtitle */}
          <p
            className="hero-subtitle text-white/85 mb-10 max-w-xl"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 400,
              fontSize: 'clamp(1rem, 2vw, 1.2rem)',
              lineHeight: 1.7
            }}>
            
            Le réseau des Centres Médicaux César De Paepe vous accueille à Bruxelles et en Brabant flamand pour tous vos besoins de santé - au tarif conventionné, avec humanité, accessibilité et expertise.
          </p>

          {/* CTAs */}
          <div className="hero-cta flex flex-wrap gap-4">
            <a
              href="#centres"
              className="btn-white text-sm"
              aria-label="Trouver un centre médical CMMC CDP">
              
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              Trouver un centre
            </a>
            <a
              href="#services"
              className="btn-outline-white text-sm"
              aria-label="Découvrir nos services médicaux">
              
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
              Nos services
            </a>
          </div>

          {/* Stats */}
          <div className="hero-cta mt-12 flex flex-wrap gap-8">
            {[
            { value: String(centresCount), label: 'Centres à Bruxelles et Brabant flamand' },
            { value: String(prestatairesCount), label: 'Prestataires de soins' },
            { value: '30 000+', label: 'Patients accompagnés' }]?.
            map((stat) =>
            <div key={stat?.label} className="flex items-center gap-3">
                <div className="w-px h-10 bg-white/30" aria-hidden="true" />
                <div>
                  <div className="text-white font-bold text-xl leading-none" style={{ fontFamily: 'Poppins, sans-serif' }}>{stat?.value}</div>
                  <div className="text-white/60 text-xs mt-0.5" style={{ fontFamily: 'Poppins, sans-serif' }}>{stat?.label}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}
